import React, { useEffect } from 'react'
import { useFragment, graphql } from 'react-relay'

import { Separator, YStack, XStack, StackProps, useThemeName } from 'tamagui'

import { useLocation } from 'react-router-dom'

import { HOST_NAME } from '../data/config/url'

import { Card } from './Card'
import { H3, Paragraph } from './Text'
import { ExternalLink } from './Link'
import { Image } from './Image'

import { SponsorsSection_query$key } from './__generated__/SponsorsSection_query.graphql'
import { SponsorsSectionSponsor_guildSponsorship$key } from './__generated__/SponsorsSectionSponsor_guildSponsorship.graphql'

export const SponsorsSection = ({
  query,
  ...props
}: {
  query: SponsorsSection_query$key
} & StackProps) => {
  const data = useFragment(
    graphql`
      fragment SponsorsSection_query on Query {
        sponsorships(first: 2) {
          edges {
            node {
              ...SponsorsSectionSponsor_guildSponsorship
            }

            cursor
          }
        }
      }
    `,
    query
  )

  if (!data.sponsorships?.edges.length) {
    return null
  }

  return (
    <YStack {...props} gap='$4' data-nosnippet>
      <Paragraph size='$4' fontWeight='$5' color='$colorMuted'>
        {data.sponsorships.edges.length === 1
          ? 'Platform Sponsor'
          : 'Platform Sponsors'}
      </Paragraph>

      <YStack gap='$4'>
        {data.sponsorships.edges.map((edge, index) => {
          if (!edge.node) {
            return null
          }

          return (
            <React.Fragment key={edge.cursor}>
              {index !== 0 && <Separator borderColor='$borderColor' />}

              <Sponsor sponsor={edge.node} />
            </React.Fragment>
          )
        })}
      </YStack>
    </YStack>
  )
}

const Sponsor = ({
  sponsor,
}: {
  sponsor: SponsorsSectionSponsor_guildSponsorship$key
}) => {
  const data = useFragment(
    graphql`
      fragment SponsorsSectionSponsor_guildSponsorship on GuildSponsorship {
        rowId
        description
        webUrl
        linkText

        lightModeImage {
          width
          height
          # Sponsor logo: max 150x80, contain fit, left focus
          transformUrl(
            transform: { w: 150, h: 80, dpr: 3, fit: "contain", fo: "left" }
          )
        }

        darkModeImage {
          width
          height
          transformUrl(
            transform: { w: 150, h: 80, dpr: 3, fit: "contain", fo: "left" }
          )
        }

        guild {
          name
        }
      }
    `,
    sponsor
  )

  const themeName = useThemeName()
  const isDark = themeName === 'dark'
  const sponsorImage = isDark && data.darkModeImage?.transformUrl
    ? data.darkModeImage
    : data.lightModeImage

  const location = useLocation()

  useEffect(() => {
    externalTrackView(
      data.rowId,
      location.state?.backgroundLocation?.pathname || location.pathname
    )
  }, [
    data.rowId,
    location.state?.backgroundLocation?.pathname || location.pathname,
  ])

  const TRACKING_URL = `${HOST_NAME}/tw/${data.rowId}`

  return (
    <YStack
      tag='a'
      gap='$4'
      cursor='pointer'
      href={TRACKING_URL}
      target='_blank'
      style={{
        textDecorationLine: 'none',
      }}
    >
      {sponsorImage?.transformUrl ? (
        <Image
          alt={`Sponsor Logo for ${data.guild?.name}`}
          src={sponsorImage.transformUrl}
          maxWidth={150}
          maxHeight={80}
          objectFit='contain'
          style={{ objectPosition: 'left' }}
        />
      ) : (
        <XStack gap='$4'>
          <H3 size='$6'>{data.guild?.name}</H3>
        </XStack>
      )}

      <Paragraph size='$4'>{data.description}</Paragraph>

      <Paragraph
        size='$4'
        color='$primaryColor'
        cursor='pointer'
        hoverStyle={{ textDecorationLine: 'underline' }}
      >
        {data.linkText}
      </Paragraph>
    </YStack>
  )
}

const TRACK_VIEW_TIMERS: Record<string, NodeJS.Timeout> = {}

const externalTrackView = async (
  guildSponsorshipId: string,
  pathname: string
) => {
  const key = [guildSponsorshipId, pathname].join('-')

  const existingTimerID = TRACK_VIEW_TIMERS[key]

  if (existingTimerID) {
    clearTimeout(existingTimerID)
  }

  TRACK_VIEW_TIMERS[key] = setTimeout(async () => {
    try {
      await fetch(`${HOST_NAME}/tv/${guildSponsorshipId}`)
    } catch (err) {}

    delete TRACK_VIEW_TIMERS[key]
  }, 100)
}
